import * as Sentry from '@sentry/react';
import escapeRegExp from 'lodash/escapeRegExp';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router-dom';
import smoothscroll from 'smoothscroll-polyfill';

import { type Level, registerErrorNotifier } from '@front/common/src/errors/error-notifier';
import type { User } from '@front/common/src/types/commons';

import { web } from '@front/i18n';

import { initializeWebApp } from '@front/web-ui/src/utils/initialize';

import App from './app';
import initializeStore from './store';
import Config from './utils/config';
import { initializeTracking } from './utils/tracking';

initializeTracking(
  import.meta.env.VITE_REACT_APP_AMPLITUDE_API_KEY || '',
  import.meta.env.VITE_REACT_APP_AMPLITUDE_TRACKING_SERVER_URL || '',
);

smoothscroll.polyfill();

const tracePropagationTargetsSources = [
  'https://www.wecasa.fr/api',
  'https://aws-stage.wecasa.fr/api',
  'https://www.wecasa.co.uk/api',
  'https://aws-stage.wecasa.co.uk/api',
  'https://www.wecasa.de/api',
  'https://aws-stage.wecasa.de/api',
  'https://www.wecasa.at/api',
  'https://aws-stage.wecasa.at/api',
  'https://www.wecasa.ch/api',
  'https://aws-stage.wecasa.ch/api',
].map((url) => new RegExp(`^${escapeRegExp(url)}`));

Sentry.init({
  dsn: Config.SENTRY_API_KEY,
  environment: import.meta.env.MODE === 'production' ? 'production' : 'staging',
  sendDefaultPii: true,
  integrations: [
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
    }),
  ],
  // Performance Monitoring
  tracesSampleRate: 0, // Temporarily set to 0 to leave room for performance monitoring of other apps
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: ['localhost', ...tracePropagationTargetsSources],
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%.
  replaysOnErrorSampleRate: 1.0, // Change the sample rate to 100% when sampling sessions where errors occur.
});

registerErrorNotifier({
  notify: (error: Error, level?: Level) => Sentry.captureException(error, { level }),
  setUser: (user: User | null) =>
    user
      ? Sentry.setUser({ id: user.public_id, email: user.email, username: `${user.first_name} ${user.last_name}` })
      : Sentry.setUser(null),
});

/*
 * Here we need to wait for the end of the initialization before we render the components
 * to be sure the translations are correctly initialized before we use them.
 * As the initialization is not inside an async context we cannot use await.
 * This is why we do a then here
 */
initializeWebApp({
  appName: Config.APP_NAME,
  i18nConfig: { translations: web },
}).then(() => {
  const { store, persistor } = initializeStore();
  const root = createRoot(document.getElementById('root')!);
  root.render(<App store={store} persistor={persistor} />);
});
